<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader />
  </div>
  <div v-else class="view category-view">
    <div class="cs-header">
      <h1 class="cs-title">Set up your categories</h1>
      <p class="cs-sub">
        Select what fits your life — you can always edit these later.
      </p>
      <span class="cs-tip"
        >You can add or remove categories any time from settings.</span
      >
    </div>

    <button
      role="button"
      aria-label="submit selected categories"
      class="cs-submit"
      @click="submitCategories"
    >
      Looks good, let's go →
    </button>

    <div
      v-for="categoryGroup in suggestedCategories"
      :key="categoryGroup.Category.name"
      class="cs-group"
      :class="{ selected: categoryGroup.Category.selected }"
    >
      <!-- Category header row -->
      <div
        class="cs-group-header"
        @click="toggleCategory(categoryGroup.Category)"
      >
        <div class="cs-group-left">
          <span class="cs-group-name">{{ categoryGroup.Category.name }}</span>
          <span
            v-if="
              categoryGroup.SubcategoriesWithRulesList &&
              categoryGroup.SubcategoriesWithRulesList.length
            "
            class="cs-group-count"
          >
            {{ selectedSubCount(categoryGroup) }}/{{
              categoryGroup.SubcategoriesWithRulesList.length
            }}
          </span>
        </div>
        <div class="cs-toggle" :class="{ on: categoryGroup.Category.selected }">
          <div class="cs-toggle-knob"></div>
        </div>
      </div>

      <!-- Subcategory chips -->
      <div
        v-if="
          categoryGroup.Category.selected &&
          categoryGroup.SubcategoriesWithRulesList &&
          categoryGroup.SubcategoriesWithRulesList.length
        "
        class="cs-subs"
      >
        <div
          v-for="subcategory in categoryGroup.SubcategoriesWithRulesList"
          :key="subcategory.Subcategory.name"
          class="cs-sub-chip"
          :class="{ on: subcategory.Subcategory.selected }"
          @click.stop="
            toggleSubcategory(categoryGroup.Category, subcategory.Subcategory)
          "
        >
          <div class="cs-sub-chip-dot"></div>
          {{ subcategory.Subcategory.name }}
        </div>
      </div>
    </div>

    <ProgressBar
      :currentStep="2"
      :steps="4"
      :stepLabels="['Account Type', 'Your Details', 'Categories', 'All Done']"
    />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useMyStore } from '../../store.js';
import SkeletonLoader from '../../components/SkeletonLoader.vue';
import ProgressBar from '../../components/ProgressBar.vue';

export default {
  components: { SkeletonLoader, ProgressBar },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const loading = ref(true);

    const selectedSubCount = (categoryGroup) => {
      return categoryGroup.SubcategoriesWithRulesList.filter(
        (s) => s.Subcategory.selected
      ).length;
    };

    let suggestedCategories = ref([
      {
        Category: {
          name: 'Food',
          icon: 'fas carrot',
          iconColor: '#8B0000',
          income: false,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [
          {
            Subcategory: {
              name: 'Groceries',
              icon: 'fas cart-shopping',
              iconColor: '#B22222',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Safeway',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Kroger',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Albertsons',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Whole Foods',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: "Trader Joe's",
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Walmart',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Costco',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Restaurants',
              icon: 'fas utensils',
              iconColor: '#DC143C',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Grubhub',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'OpenTable',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Fast Food',
              icon: 'fas burger',
              iconColor: '#FF0000',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: "McDonald's",
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Burger King',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Chick-fil-A',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Taco Bell',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: "Wendy's",
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Coffee',
              icon: 'fas mug-saucer',
              iconColor: '#FF4500',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Starbucks',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Dutch Bros',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: "Peet's Coffee",
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Alcohol',
              icon: 'fas wine-glass',
              iconColor: '#FF6347',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Total Wine',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'BevMo',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Snacks',
              icon: 'fas cookie',
              iconColor: '#FF7F50',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: '7-Eleven',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Circle K',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Meal Delivery',
              icon: 'fas truck',
              iconColor: '#FFA07A',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'DoorDash',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Uber Eats',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Meal Kits',
              icon: 'fas kitchen-set',
              iconColor: '#8B4513',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'HelloFresh',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Blue Apron',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Home Chef',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
        ],
      },
      {
        Category: {
          name: 'Income',
          icon: 'fas money-bill-1-wave',
          iconColor: '#A0522D',
          income: true,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [
          {
            Subcategory: {
              name: 'Salary',
              icon: 'fas money-check',
              iconColor: '#CD853F',
              income: true,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Commissions',
              icon: 'fas comments-dollar',
              iconColor: '#FF8C00',
              income: true,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Tips',
              icon: 'fas coins',
              iconColor: '#FFA500',
              income: true,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Side Hustles',
              icon: 'fas money-bill-transfer',
              iconColor: '#FFB347',
              income: true,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Investments',
              icon: 'fas seedling',
              iconColor: '#FFD700',
              income: true,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Vanguard',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Fidelity',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Charles Schwab',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Gifts',
              icon: 'fas circle-dollar-to-slot',
              iconColor: '#FFFF00',
              income: true,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Tax Refunds',
              icon: 'fas receipt',
              iconColor: '#FFFFE0',
              income: true,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'IRS',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Alimony Received',
              icon: 'fas ring',
              iconColor: '#FFF68F',
              income: true,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Child Support Received',
              icon: 'fas baby-carriage',
              iconColor: '#FFFACD',
              income: true,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Bonus',
              icon: 'fas sack-dollar',
              iconColor: '#FFFAF0',
              income: true,
              selected: true,
            },
            Rules: [],
          },
        ],
      },
      {
        Category: {
          name: 'Utilities',
          icon: 'fas lightbulb',
          iconColor: '#ffd700',
          income: false,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [
          {
            Subcategory: {
              name: 'Electricity',
              icon: 'fas bolt-lightning',
              iconColor: '#006400',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Idaho Power',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Water',
              icon: 'fas water',
              iconColor: '#228B22',
              income: false,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Gas',
              icon: 'fas fire-flame-simple',
              iconColor: '#32CD32',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Intermountain Gas',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Internet',
              icon: 'fas wifi',
              iconColor: '#00FF00',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Xfinity',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'CenturyLink',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Cable',
              icon: 'fas tv',
              iconColor: '#7CFC00',
              income: false,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Phone',
              icon: 'fas phone',
              iconColor: '#90EE90',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Verizon',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'AT&T',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'T-Mobile',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Trash',
              icon: 'fas trash',
              iconColor: '#98FB98',
              income: false,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Sewer',
              icon: 'fas poo',
              iconColor: '#E0FFBF',
              income: false,
              selected: true,
            },
            Rules: [],
          },
        ],
      },
      {
        Category: {
          name: 'Transportation',
          icon: 'fas truck-plane',
          iconColor: '#000080',
          income: false,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [
          {
            Subcategory: {
              name: 'Fuel',
              icon: 'fas gas-pump',
              iconColor: '#0000CD',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Shell',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Chevron',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Exxon',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'BP',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Public Transportation',
              icon: 'fas bus',
              iconColor: '#1E90FF',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Metro',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Subway',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'BART',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Ride Shares',
              icon: 'fas car',
              iconColor: '#4169E1',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Uber',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Lyft',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Car Payment',
              icon: 'fas car-side',
              iconColor: '#87CEEB',
              income: false,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Car Maintenance',
              icon: 'fas wrench',
              iconColor: '#ADD8E6',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Jiffy Lube',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Meineke',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Midas',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Car Insurance',
              icon: 'fas car-burst',
              iconColor: '#B0E0E6',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'GEICO',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'State Farm',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Progressive',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Allstate',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Parking',
              icon: 'fas square-parking',
              iconColor: '#E6F2FF',
              income: false,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Road Tolls',
              icon: 'fas road',
              iconColor: '#4B0082',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'EZPass',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'FasTrak',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
        ],
      },
      {
        Category: {
          name: 'Health',
          icon: 'fas user-nurse',
          iconColor: '#6A5ACD',
          income: false,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [
          {
            Subcategory: {
              name: 'Health Insurance',
              icon: 'fas notes-medical',
              iconColor: '#7B68EE',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Blue Cross',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'UnitedHealthcare',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Aetna',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Dental Insurance',
              icon: 'fas tooth',
              iconColor: '#8A2BE2',
              income: false,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Vision Insurance',
              icon: 'fas eye',
              iconColor: '#9370DB',
              income: false,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Prescriptions',
              icon: 'fas pills',
              iconColor: '#BA55D3',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'CVS',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Walgreens',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Rite Aid',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Gym Membership',
              icon: 'fas dumbbell',
              iconColor: '#D8BFD8',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Planet Fitness',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: '24 Hour Fitness',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'LA Fitness',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Therapy',
              icon: 'far face-smile',
              iconColor: '#E6E6FA',
              income: false,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Personal Trainer',
              icon: 'fas person-running',
              iconColor: '#800080',
              income: false,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Massage',
              icon: 'fas spa',
              iconColor: '#8B008B',
              income: false,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Chiropractor',
              icon: 'fas x-ray',
              iconColor: '#9400D3',
              income: false,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Acupuncture',
              icon: 'fas leaf',
              iconColor: '#BA55D3',
              income: false,
              selected: true,
            },
            Rules: [],
          },
        ],
      },
      {
        Category: {
          name: 'Housing',
          icon: 'fas house',
          iconColor: '#BA55D3',
          income: false,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [
          {
            Subcategory: {
              name: 'Rent/Mortgage',
              icon: 'fas house',
              iconColor: '#DA70D6',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Mortgage',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Apartment',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Rent Payment',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Home Maintenance',
              icon: 'fas hammer',
              iconColor: '#DDA0DD',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Home Depot',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: "Lowe's",
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Ace Hardware',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Home Improvement',
              icon: 'fas paint-roller',
              iconColor: '#EE82EE',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Home Depot',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: "Lowe's",
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Menards',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Property Taxes',
              icon: 'fas file-invoice',
              iconColor: '#F3E5F5',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'County Tax',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Tax Collector',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'HOA Fees',
              icon: 'fas handshake',
              iconColor: '#C71585',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'HOA',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Homeowners Association',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
        ],
      },
      {
        Category: {
          name: 'Travel',
          icon: 'fas globe',
          iconColor: '#DB7093',
          income: false,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [
          {
            Subcategory: {
              name: 'Flights',
              icon: 'fas plane',
              iconColor: '#FF1493',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Delta',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'American Airlines',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'United Airlines',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Southwest',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Alaska Airlines',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Accommodation',
              icon: 'fas bell-concierge',
              iconColor: '#FF69B4',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Marriott',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Hilton',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Airbnb',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'VRBO',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Transportation',
              icon: 'fas truck-plane',
              iconColor: '#FFB6C1',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Hertz',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Enterprise',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Avis',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Budget Car Rental',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Food',
              icon: 'fas carrot',
              iconColor: '#FFC0CB',
              income: false,
              selected: true,
            },
            Rules: [],
          },
          {
            Subcategory: {
              name: 'Activities',
              icon: 'fas star',
              iconColor: '#FFD1DC',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Viator',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'GetYourGuide',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
        ],
      },
      {
        Category: {
          name: 'Gifts',
          icon: 'fas gift',
          iconColor: '#ff7f50',
          income: false,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [
          {
            Subcategory: {
              name: 'Birthdays',
              icon: 'fas cake-candles',
              iconColor: '#5D4037',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Hallmark',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Party City',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Christmas',
              icon: 'fas gift',
              iconColor: '#6D4C41',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Christmas Tree Shops',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Amazon',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Weddings',
              icon: 'fas gem',
              iconColor: '#795548',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Bed Bath & Beyond',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Zola',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Crate & Barrel',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
        ],
      },
      {
        Category: {
          name: 'Clothing',
          icon: 'fas shirt',
          iconColor: '#8D6E63',
          income: false,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [],
      },
      {
        Category: {
          name: 'Debts',
          icon: 'fas money-bill-transfer',
          iconColor: '#5D50A8',
          income: false,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [
          {
            Subcategory: {
              name: 'Credit Cards',
              icon: 'fas credit-card',
              iconColor: '#BCAAA4',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Chase',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Capital One',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'American Express',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Discover',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Personal Loans',
              icon: 'fas money-check-dollar',
              iconColor: '#D7CCC8',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'SoFi',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'LendingClub',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Upstart',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
        ],
      },
      {
        Category: {
          name: 'Education',
          icon: 'fas school-flag',
          iconColor: '#E66B89',
          income: false,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [
          {
            Subcategory: {
              name: 'Tuition',
              icon: 'fas school',
              iconColor: '#EFEBE9',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'University',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'College',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'School Tuition',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Books/Supplies',
              icon: 'fas book',
              iconColor: '#212121',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Barnes & Noble',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Amazon',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Courses',
              icon: 'fas graduation-cap',
              iconColor: '#424242',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Udemy',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Coursera',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Skillshare',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Student Loans',
              icon: 'fas scroll',
              iconColor: '#616161',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Navient',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Nelnet',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Great Lakes',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
        ],
      },
      {
        Category: {
          name: 'Kids',
          icon: 'fas children',
          iconColor: '#00695C',
          income: false,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [
          {
            Subcategory: {
              name: 'Childcare',
              icon: 'fas baby',
              iconColor: '#757575',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Daycare',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Preschool',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Babysitting',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Toys',
              icon: 'fas robot',
              iconColor: '#9E9E9E',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Toys R Us',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Target',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Walmart',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Activities',
              icon: 'fas person-biking',
              iconColor: '#BDBDBD',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Swim School',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Soccer Club',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Dance Academy',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Allowance',
              icon: 'fas hand-sparkles',
              iconColor: '#E0E0E0',
              income: false,
              selected: true,
            },
            Rules: [],
          },
        ],
      },
      {
        Category: {
          name: 'Pets',
          icon: 'fas fish-fins',
          iconColor: '#006064',
          income: false,
          selected: true,
        },
        CategoryRules: [],
        SubcategoriesWithRulesList: [
          {
            Subcategory: {
              name: 'Food',
              icon: 'fas bowl-food',
              iconColor: '#00838F',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Petco',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'PetSmart',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Chewy',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Toys',
              icon: 'fas bone',
              iconColor: '#0097A7',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Petco',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'PetSmart',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Pet Insurance',
              icon: 'fas dog',
              iconColor: '#00ACC1',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Healthy Paws',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'Trupanion',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
          {
            Subcategory: {
              name: 'Pet Care',
              icon: 'fas shield-dog',
              iconColor: '#00BCD4',
              income: false,
              selected: true,
            },
            Rules: [
              {
                ComparisonValue: 'Banfield Pet Hospital',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
              {
                ComparisonValue: 'VCA Animal Hospital',
                Type: 'Name',
                Comparison: 'Contains',
                MatchType: 'Contains',
              },
            ],
          },
        ],
      },
    ]);

    const submitCategories = () => {
      loading.value = true;
      let categoryWithSubcategoriesList = [];

      for (let i = 0; i < suggestedCategories.value.length; i++) {
        const categoryGroup = suggestedCategories.value[i];

        if (categoryGroup.Category.selected) {
          let categoryWithSubcategories = {
            Category: {
              Name: categoryGroup.Category.name,
              Icon: categoryGroup.Category.icon || null,
              IconColor: categoryGroup.Category.iconColor || null,
              Income: categoryGroup.Category.income,
            },
            Rules: categoryGroup.CategoryRules || [],
            SubcategoriesWithRulesList: [],
          };

          if (
            categoryGroup.SubcategoriesWithRulesList &&
            categoryGroup.SubcategoriesWithRulesList.length > 0
          ) {
            for (
              let j = 0;
              j < categoryGroup.SubcategoriesWithRulesList.length;
              j++
            ) {
              const subcategoryWithRules =
                categoryGroup.SubcategoriesWithRulesList[j];

              if (subcategoryWithRules.Subcategory.selected) {
                categoryWithSubcategories.SubcategoriesWithRulesList.push({
                  Subcategory: {
                    Name: subcategoryWithRules.Subcategory.name,
                    Icon: subcategoryWithRules.Subcategory.icon || null,
                    IconColor:
                      subcategoryWithRules.Subcategory.iconColor || null,
                    Income: subcategoryWithRules.Subcategory.income,
                  },
                  Rules: subcategoryWithRules.Rules || [],
                });
              }
            }
          }

          categoryWithSubcategoriesList.push(categoryWithSubcategories);
        }
      }

      store.saveSuggestedCategories(categoryWithSubcategoriesList).then(() => {
        loading.value = false;
        router.push('ConnnectAccounts');
      });
    };

    const toggleCategory = (category) => {
      const categoryIndex = suggestedCategories.value.findIndex(
        (cat) => cat.Category.name === category.name
      );
      if (categoryIndex !== -1) {
        suggestedCategories.value[categoryIndex].Category.selected =
          !suggestedCategories.value[categoryIndex].Category.selected;
        if (!suggestedCategories.value[categoryIndex].Category.selected) {
          suggestedCategories.value[
            categoryIndex
          ].SubcategoriesWithRulesList.forEach(
            (subcategory) => (subcategory.Subcategory.selected = false)
          );
        }
      }
    };

    const toggleSubcategory = (category, subcategory) => {
      const categoryIndex = suggestedCategories.value.findIndex(
        (cat) => cat.Category.name === category.name
      );

      if (categoryIndex !== -1) {
        const subcategoryIndex = suggestedCategories.value[
          categoryIndex
        ].SubcategoriesWithRulesList.findIndex(
          (sub) => sub.Subcategory.name === subcategory.name
        );

        if (subcategoryIndex !== -1) {
          suggestedCategories.value[categoryIndex].SubcategoriesWithRulesList[
            subcategoryIndex
          ].Subcategory.selected =
            !suggestedCategories.value[categoryIndex]
              .SubcategoriesWithRulesList[subcategoryIndex].Subcategory
              .selected;

          if (
            suggestedCategories.value[
              categoryIndex
            ].SubcategoriesWithRulesList.every(
              (sub) => !sub.Subcategory.selected
            )
          ) {
            suggestedCategories.value[categoryIndex].selected = false;
          } else {
            suggestedCategories.value[categoryIndex].selected = true;
          }
        }
      }
    };

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);
      loading.value = false;
    });

    return {
      store,
      suggestedCategories,
      router,
      submitCategories,
      toggleCategory,
      toggleSubcategory,
      selectedSubCount,
      loading,
    };
  },
};
</script>

<style scoped>
.category-view {
  max-width: 480px;
  margin: 0 auto;
}

.cs-header {
  text-align: center;
  padding: 1.75rem 0 0.25rem;
  margin-bottom: 0.5rem;
}

.cs-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.3rem;
  letter-spacing: -0.02em;
}

.dark-theme .cs-title {
  color: #5dcaa5;
}

.cs-sub {
  font-size: 0.85rem;
  color: #888;
  margin: 0 0 0.75rem;
}

.cs-tip {
  display: inline-block;
  background: #eeedfe;
  border-radius: 12px;
  padding: 0.5rem 1rem;
  font-size: 0.78rem;
  color: #3c3489;
  font-weight: 500;
}

.dark-theme .cs-tip {
  background: #1e1a40;
  color: #afa9ec;
}

.cs-submit {
  width: 100%;
  border: none;
  border-radius: 50px;
  padding: 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
  color: white;
  cursor: pointer;
  margin: 1.25rem 0 1.5rem;
  transition:
    opacity 0.15s,
    transform 0.15s;
}

.cs-submit:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.cs-submit:active {
  transform: scale(0.97);
}

/* Category cards */
.cs-group {
  border-radius: 14px;
  border: 2px solid #eee;
  background: white;
  margin-bottom: 0.85rem;
  overflow: hidden;
  transition: border-color 0.2s;
}

.dark-theme .cs-group {
  background: #212e3e;
  border-color: #2a3a4e;
}

.cs-group.selected {
  border-color: #7f77dd;
}

.dark-theme .cs-group.selected {
  border-color: #7f77dd;
}

.cs-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1rem;
  cursor: pointer;
  user-select: none;
}

.cs-group-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cs-group-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #004751;
}

.dark-theme .cs-group-name {
  color: #ffffff;
}

.cs-group-count {
  font-size: 11px;
  font-weight: 600;
  color: #7f77dd;
  background: #eeedfe;
  padding: 2px 8px;
  border-radius: 20px;
}

.dark-theme .cs-group-count {
  background: #1e1a40;
  color: #afa9ec;
}

/* Toggle switch */
.cs-toggle {
  width: 38px;
  height: 22px;
  border-radius: 11px;
  background: #e0e0e0;
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
}

.dark-theme .cs-toggle {
  background: #2a3a4e;
}

.cs-toggle.on {
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
}

.cs-toggle-knob {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 3px;
  left: 3px;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.cs-toggle.on .cs-toggle-knob {
  transform: translateX(16px);
}

/* Subcategory chips */
.cs-subs {
  padding: 0.75rem 1rem;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dark-theme .cs-subs {
  border-top-color: #2a3a4e;
}

.cs-sub-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  border: 1.5px solid #e0e0e0;
  background: white;
  color: #666;
  transition: all 0.15s;
  user-select: none;
  font-family: 'Poppins', sans-serif;
}

.dark-theme .cs-sub-chip {
  background: #1a2535;
  border-color: #2a3a4e;
  color: #6a7a8a;
}

.cs-sub-chip.on {
  border-color: #5dcaa5;
  background: #e1f5ee;
  color: #0f6e56;
}

.dark-theme .cs-sub-chip.on {
  background: #0a2a20;
  border-color: #5dcaa5;
  color: #5dcaa5;
}

.cs-sub-chip-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ccc;
  transition: background 0.15s;
  flex-shrink: 0;
}

.cs-sub-chip.on .cs-sub-chip-dot {
  background: #5dcaa5;
}
</style>
